import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import ActionableCard from 'tio-ui/components/actionable-card';
import HighlightCard from 'tio-ui/components/highlight-card';
import GlassCard from 'tio-ui/components/glass-card';
import StepCard from 'tio-ui/components/step-card';
import TaEligibilityBadge from 'tio-ui/components/ta-eligibility-badge';
import {
  PhArrowLeft,
  PhArrowRight,
  PhCheckCircle,
  PhClock,
  PhCurrencyDollar,
  PhGraduationCap,
  PhGridFour,
  PhHeart,
  PhLayout,
  PhSparkle,
  PhTrendUp,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="tio-inline-row gap-2 tio-fg-brand dark:text-violet-400 hover:tio-fg-brand dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Card Components"
      @subtitle="Rich card patterns for courses, benefits, stats, and onboarding steps."
      @icon={{PhGridFour}}
      @gradient="cool"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">4 Components</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            ActionableCard
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhLayout @size={{20}} class="tio-fg-brand" />
            <span>ActionableCard</span>
            <Chip @size="sm" @intent="primary">Complex</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Card with header badge, match score, metadata row, skill chips, eligibility, and
            actions. Used for courses, certifications, and job listings.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4">
            <ActionableCard
              @title="Nurse Leadership Certificate"
              @matchScore={{98}}
              @showBanner={{true}}
              @bannerText="Top Pick for You"
              class="max-w-2xl"
            >
              <:badge><Chip @intent="success">Best Match</Chip></:badge>
              <:metadata>
                <div class="tio-row-loose text-sm tio-fg-muted">
                  <span class="tio-row-extra-tight"><PhGraduationCap @size={{16}} />ANCC</span>
                  <span class="tio-row-extra-tight"><PhClock @size={{16}} />4 months</span>
                  <span class="tio-row-extra-tight"><PhCurrencyDollar @size={{16}} />$1,850</span>
                </div>
              </:metadata>
              <:skills>
                <Chip @size="sm">Leadership</Chip>
                <Chip @size="sm">Team Management</Chip>
                <Chip @size="sm">Quality Improvement</Chip>
              </:skills>
              <:eligibility>
                <TaEligibilityBadge @eligible={{true}} @amount="$1,850 covered" />
              </:eligibility>
              <:actions>
                <Button @intent="default" @appearance="minimal" @size="sm">
                  <PhHeart @size={{20}} /><span class="text-sm">Save</span>
                </Button>
                <div class="tio-flex-tight">
                  <Button @intent="default" @appearance="outlined" @size="sm">Learn More</Button>
                  <Button @intent="primary" @size="sm" @icon={{PhArrowRight}} @label="Enroll Now" />
                </div>
              </:actions>
            </ActionableCard>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >&lt;ActionableCard @title="Nurse Leadership" @matchScore=&#123;&#123;98&#125;&#125;
                @showBanner=&#123;&#123;true&#125;&#125; @bannerText="Top Pick"&gt;
                &lt;:badge&gt;&lt;Chip @intent="success"&gt;Best Match&lt;/Chip&gt;&lt;/:badge&gt;
                &lt;:metadata&gt;...&lt;/:metadata&gt; &lt;:skills&gt;&lt;Chip
                @size="sm"&gt;Leadership&lt;/Chip&gt;&lt;/:skills&gt;
                &lt;:eligibility&gt;&lt;TaEligibilityBadge @eligible=&#123;&#123;true&#125;&#125;
                /&gt;&lt;/:eligibility&gt; &lt;:actions&gt;...&lt;/:actions&gt;
                &lt;/ActionableCard&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            HighlightCard
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhSparkle @size={{20}} class="tio-fg-brand" />
            <span>HighlightCard</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Gradient-background card for showcasing benefits or key features. Icon, title, value,
            description, with optional extra block.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <HighlightCard
                @icon={{PhCurrencyDollar}}
                @title="Annual Benefit"
                @value="$5,250"
                @description="Tax-free per year"
                @color="emerald"
              />
              <HighlightCard
                @icon={{PhGraduationCap}}
                @title="Programs"
                @value="12"
                @description="Eligible options"
                @color="violet"
              />
              <HighlightCard
                @icon={{PhTrendUp}}
                @title="Savings"
                @value="$18K"
                @description="Lifetime total"
                @color="blue"
              />
              <HighlightCard
                @icon={{PhCheckCircle}}
                @title="Approval Rate"
                @value="94%"
                @description="This quarter"
                @color="amber"
              />
            </div>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >&lt;HighlightCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @title="Annual
                Benefit" @value="$5,250" @description="Tax-free per year" @color="emerald" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            GlassCard
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhSparkle @size={{20}} class="tio-fg-brand" />
            <span>GlassCard</span>
            <Chip @size="sm" @intent="success">For Dark BGs</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Translucent frosted-glass stat card designed for dark/hero background contexts. Pairs
            well with PageHero.
          </Text>

          <div class="tio-gradient-brand rounded-lg p-6 mb-4">
            <div class="grid grid-cols-3 gap-4">
              <GlassCard
                @icon={{PhCurrencyDollar}}
                @label="Available"
                @value="$5,250"
                @subtext="Annual limit"
                @accent="default"
              />
              <GlassCard
                @icon={{PhCheckCircle}}
                @label="Used"
                @value="$2,100"
                @subtext="This year"
                @accent="default"
              />
              <GlassCard
                @icon={{PhTrendUp}}
                @label="Savings"
                @value="$18K"
                @subtext="Lifetime"
                @accent="default"
              />
            </div>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >{{! Place inside a PageHero or dark gradient }}
                &lt;GlassCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @label="Available"
                @value="$5,250" @subtext="Annual limit" @accent="default" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            StepCard
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhCheckCircle @size={{20}} class="tio-fg-brand" />
            <span>StepCard</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Onboarding/progress step card with numbered indicator. Supports completed and active
            states.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-3">
            <StepCard
              @step={{1}}
              @title="Create Account"
              @description="Set up your profile and preferences"
              @completed={{true}}
            >
              <:action>
                <Chip @intent="success" @size="sm">Done</Chip>
              </:action>
            </StepCard>
            <StepCard
              @step={{2}}
              @title="Submit Application"
              @description="Choose a program and apply for TA"
              @active={{true}}
            >
              <:action>
                <Button @intent="primary" @size="sm">Start</Button>
              </:action>
            </StepCard>
            <StepCard
              @step={{3}}
              @title="Track Progress"
              @description="Monitor your application status"
            />
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >&lt;StepCard @step=&#123;&#123;1&#125;&#125; @title="Create Account"
                @description="Set up your profile" @completed=&#123;&#123;true&#125;&#125;&gt;
                &lt;:action&gt;&lt;Chip @intent="success"&gt;Done&lt;/Chip&gt;&lt;/:action&gt;
                &lt;/StepCard&gt;</code></pre>
          </details>
        </:body>
      </Section>
    </div>
  </VStack>
</template>
